import styled from "@emotion/styled";
import type { MinimalTokenInfo } from "@stepfinance/common";
import { SRM_MINT } from "@stepfinance/common";
import { orange } from "@stepfinance/react";
import type { FC, ReactNode } from "react";
import { useMemo } from "react";

import { useLanguageContext } from "../../contexts/language/language";

type TokenWarning = {
  mints: string[];
  predicate?: (mint: MinimalTokenInfo | undefined) => boolean;
  message: ReactNode;
};

type SwapTokenWarningProps = {
  mintA?: MinimalTokenInfo;
  mintB?: MinimalTokenInfo;
};

export const SwapTokenWarning: FC<SwapTokenWarningProps> = ({ mintA, mintB }) => {
  const {
    currentLanguageData: {
      pages: {
        swapLP: { tokenWarnings },
      },
    },
  } = useLanguageContext();

  const warning = useMemo(() => {
    const warningList: TokenWarning[] = [
      {
        mints: [],
        predicate: (mint) => {
          return (mint && mint.tags?.includes("wrapped-sollet")) ?? false;
        },
        message: tokenWarnings.solletTokens.message,
      },
      {
        mints: [SRM_MINT.toBase58()],
        message: tokenWarnings.srm.message,
      },
    ];

    return warningList.find((warning) => {
      const satisfiesPredicate = warning.predicate?.(mintA) || warning.predicate?.(mintB) || false;
      const includedInMintList =
        warning.mints.includes(mintA?.address ?? "") || warning.mints.includes(mintB?.address ?? "");

      return satisfiesPredicate || includedInMintList;
    });
  }, [mintA, mintB, tokenWarnings]);

  return warning ? (
    <WarningBox>
      <span>{warning.message}</span>
    </WarningBox>
  ) : null;
};

const WarningBox = styled.div`
  padding: 9px 16px;
  margin-bottom: 20px;

  background: ${orange};
  color: white;
  border-radius: 8px;
`;
